import CloseIcon from "@mui/icons-material/Close";
import { Backdrop, CircularProgress, Divider, Grid } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { DateInput } from "qubCommon/Inputs";
import AmkaInput from "qubCommon/Inputs/AmkaInput";
import RadioInput from "qubCommon/Inputs/RadioInput";
import { useEffect, useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { useLookup } from "src/context/lookupContext";
import DynamicDialog from "src/qubCommon/Dialogs/DynamicDialog";
import Dropdown from "src/qubCommon/Inputs/Dropdown";
import TextInput from "src/qubCommon/Inputs/TextInput";
import { v4 as uuidv4 } from "uuid";
import AlertBanner from "../AlertBanner";
import { Sync } from "@mui/icons-material";
import { getChildAmkaDataIfGuardian } from "apis/controllers/applications";
import { useParams } from "react-router-dom";

const CHILD_NOT_INSURED_BY_APPLICANT = "CHILD_NOT_INSURED_BY_APPLICANT";
const CHILD_SEARCH_UNAVAILABLE = "CHILD_SEARCH_UNAVAILABLE";

export default function ChildFormDialog(props) {
  const {
    open,
    handleClose,
    childForm,
    handleAddRow,
    handleEditRow,
    canEdit,
    canPartiallyEdit,
    guardianOptions = [],
    amkaOptions = [],
  } = props;
  const { lookups } = useLookup();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialog, setDialog] = useState({
    title: "Σφάλμα",
    message: "Σφάλμα",
    type: "error",
  });
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [warningDialog, setWarningDialog] = useState({
    title: "Προειδοποίηση",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [parentIsValid, setParentIsValid] = useState(false);
  const [amkaMapped, setAmkaMapped] = useState(false);
  const { id } = useParams();
  const editId = childForm?.getValues("id");
  const customCanEdit = childForm.getValues("canEdit") !== false;
  const applicantGuardianId = guardianOptions?.[0]?.id;
  const guardianId = useWatch({
    control: childForm.control,
    name: "guardianId",
  });
  const hasGuardianId =
    guardianId !== undefined && guardianId !== null && guardianId !== "";
  const hasDifferentGuardianSelected =
    hasGuardianId && String(guardianId) !== String(applicantGuardianId);

  const showSearchDialog = ({ title, message, type = "error" }) => {
    setDialog({
      title,
      message,
      type,
      onAccept: () => setDialogOpen(false),
    });
    setDialogOpen(true);
  };

  const clearApplicantGuardianSelection = () => {
    const currentGuardianId = childForm.getValues("guardianId");

    if (
      applicantGuardianId &&
      String(currentGuardianId) === String(applicantGuardianId)
    ) {
      childForm.setValue("guardianId", null, { shouldDirty: true });
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const findLookupObject = (
    lookupTable,
    value,
    fieldName,
    extraFields = []
  ) => {
    let intValue;
    try {
      intValue = parseInt(value, 10);
    } catch (e) {
      console.log(e);
    }
    const selectedObject = lookupTable?.find(
      (record) => record?.id === intValue
    );
    childForm.setValue(fieldName, selectedObject.name);
    extraFields.forEach((field) => {
      childForm.setValue(field?.name, selectedObject[field?.label]);
    });
  };

  const getAgeAtYearEnd = (dateStr) => {
    if (!dateStr) return null;

    const dob = new Date(dateStr);
    if (isNaN(dob.getTime())) return null; // invalid date check

    const currentYear = new Date().getFullYear();
    const endOfYear = new Date(currentYear, 11, 31); // 31/12

    let age = endOfYear.getFullYear() - dob.getFullYear();
    if (
      endOfYear.getMonth() < dob.getMonth() ||
      (endOfYear.getMonth() === dob.getMonth() &&
        endOfYear.getDate() < dob.getDate())
    ) {
      age--;
    }

    return age;
  };

  const dob = childForm.watch("dob.datePickerFormatted");
  const genderId = childForm.watch("gender.id");

  const age = dob ? getAgeAtYearEnd(dob) : null;
  const isFemale = genderId === 2;

  const computedCategoryOptions = (lookups?.childCategories || []).map(
    (opt) => {
      const id = opt.id;

      // Default: enabled
      let disabled = false;

      if (age !== null) {
        if (age <= 18) {
          // ONLY "Ανήλικο" allowed
          disabled = id !== 1;
        } else if (age > 18 && id === 1) {
          // Disable "Ανήλικο" if overage
          disabled = true;
        }

        if (age > 24) {
          // Disable everything EXCEPT ΑμεΑ (id 4)
          disabled = id !== 4 && id !== 8;
        }
      }

      // Rule: if female, disable "Στρατευμένο" (id 3)
      if (isFemale && id === 3) {
        disabled = true;
      }

      return {
        ...opt,
        disabled,
      };
    }
  );

  const handleAmkaMap = async () => {
    try {
      setLoading(true);
      const res = await getChildAmkaDataIfGuardian(
        id,
        childForm.getValues("amka")
      );

      if (res?.status === "error") {
        const responseData = res?.data || {};
        const isBlockingResult = responseData?.isBlocking === true;
        const errorCode = responseData?.errorCode;

        setAmkaMapped(false);

        if (
          isBlockingResult &&
          errorCode === CHILD_NOT_INSURED_BY_APPLICANT
        ) {
          clearApplicantGuardianSelection();
          showSearchDialog({
            title: "Απαιτείται έτερος γονέας/κηδεμόνας",
            type: "warning",
            message:
              res?.message ||
              "Δεν επιβεβαιώθηκε ασφαλιστική σχέση του τέκνου με τον/τη δικαιούχο.\nΑν το τέκνο είναι ασφαλισμένο στον έτερο γονέα, προσθέστε τον έτερο γονέα και επιλέξτε τον στο πεδίο 'Έμμεσα Ασφαλισμένο σε'.",
          });
          return;
        }

        if (
          errorCode === CHILD_SEARCH_UNAVAILABLE ||
          responseData?.isBlocking === false
        ) {
          showSearchDialog({
            title: "Η αναζήτηση δεν είναι διαθέσιμη",
            type: "warning",
            message:
              res?.message ||
              "Η αναζήτηση δεν είναι διαθέσιμη.\nΠαρακαλώ συμπληρώστε τα υπόλοιπα πεδία χειροκίνητα.",
          });
          return;
        }

        showSearchDialog({
          title: "Σφάλμα",
          message:
            res?.message ||
            "Η αναζήτηση δεν είναι διαθέσιμη.\nΠαρακαλώ συμπληρώστε τα υπόλοιπα πεδία χειροκίνητα.",
        });
        return;
      }

      if (!res?.data) {
        // Show dialog if response is null
        setDialog({
          title: "Σφάλμα",
          message:
            res?.message ||
            "Δεν βρέθηκε αυτό το ΑΜΚΑ ως έμμεσα ασφαλισμένο στην μερίδα σας.\nΠαρακαλώ συμπληρώστε τα υπόλοιπα πεδία χειροκίνητα.",
          onAccept: () => setDialogOpen(false),
        });
        setDialogOpen(true);
        return;
      }

      if (res?.status === "success") {
        childForm.setValue("firstName", res?.data?.firstName);
        childForm.setValue("lastName", res?.data?.lastName);
        childForm.setValue(
          "dob.datePickerFormatted",
          res?.data?.dob?.datePickerFormatted
        );
        childForm.setValue("gender.id", res?.data?.gender.id);
        childForm.setValue("gender.name", res?.data?.gender.name);
        childForm.setValue("guardianId", res?.data?.guardianId);
        setAmkaMapped(true);
      }
    } catch (err) {
      console.error(err);
      setDialog({
        title: "Σφάλμα",
        message:
          "Παρουσιάστηκε σφάλμα κατά την αναζήτηση.\nΠαρακαλώ συμπληρώστε τα υπόλοιπα πεδία χειροκίνητα.",
        onAccept: () => setDialogOpen(false),
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (age === null) return;

    const currentCategoryId = childForm.getValues("category.id");

    const selectedCategory = computedCategoryOptions.find(
      (opt) => opt.id === currentCategoryId
    );

    if (age <= 18) {
      // Automatically select Ανήλικο (ID 1)
      if (currentCategoryId !== 1) {
        childForm.setValue("category.id", 1);
        findLookupObject(lookups?.childCategories, 1, "category.name", [
          { name: "category.fileDataKey", label: "fileDataKey" },
          { name: "category.fileDataValue", label: "fileDataValue" },
        ]);
      }
    } else {
      // Clear selected category if it's now disabled
      if (selectedCategory?.disabled) {
        childForm.setValue("category.id", null);
        childForm.setValue("category.name", "");
        childForm.setValue("category.fileDataKey", "");
        childForm.setValue("category.fileDataValue", "");
      }
    }
  }, [age, computedCategoryOptions]);

  return (
    <FormProvider {...childForm}>
      {/* <Backdrop
        open={loading}
        sx={{
          color: (theme) => theme.palette.primary.main,
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop> */}
      <DynamicDialog
        open={warningDialogOpen}
        meta={{
          title: "Προειδοποίηση",
          description: (
            <div>
              Το Τέκνο με ιδιότητα 'Κανένα από τα παραπάνω' ενδέχεται να μην
              είναι επιλέξιμο για αυτό το πρόγραμμα ΛΑΕ ΟΠΕΚΑ.{" "}
              <strong>
                Εάν είναι απαραίτητο για την αίτηση σας (Τρίτεκνης ή Πολύτεκνης)
                θα πρέπει να εμπίπτει σε κάποια άλλη από τις υπάρχουσες
                κατηγορίες.
              </strong>
            </div>
          ),
          action: "ΟΚ",
          cancel: "Οχι",
        }}
        onAccept={warningDialog.onAccept}
        onClose={() => {
          setWarningDialogOpen(false);
          setWarningDialog({ onAccept: () => { } });
        }}
        hideSecondaryButton
        type="warning"
      />
      <DynamicDialog
        open={dialogOpen}
        meta={{
          title: dialog?.title,
          description: dialog?.message,
          action: "OK",
        }}
        onAccept={dialog.onAccept}
        hideSecondaryButton
        type={dialog?.type || "error"}
      />
      <Dialog
        onClose={() => {
          setParentIsValid(false);
          setAmkaMapped(false);
          handleClose();
        }}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Στοιχεία Τέκνου
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => {
            setParentIsValid(false);
            setAmkaMapped(false);
            handleClose();
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Divider />
        <DialogContent
          sx={{
            width: "90%",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <AlertBanner
            info={{
              message: ` <div>Η ${editId ? "Ενημέρωση" : "Προσθήκη"
                } θα πραγματοποιηθεί μετά την αποθήκευση της αίτησης.</div> `,
              cssClass: "info",
            }}
          />
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            <Grid container spacing={2}>
              {/* Row 1: amka, search button */}
              <Grid item xs={12} sm={8} md={4}>
                <AmkaInput
                  disabled={!canEdit || !customCanEdit || amkaMapped}
                  name="amka"
                  label="AMKA"
                  defaultFromProps={false}
                  setParentState={setParentIsValid}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                md={2}
                sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleAmkaMap}
                  disabled={
                    !canEdit ||
                    !customCanEdit ||
                    loading ||
                    !parentIsValid ||
                    amkaMapped ||
                    hasDifferentGuardianSelected
                  }
                  startIcon={
                    loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <Sync />
                    )
                  }
                >
                  Αναζητηση
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Dropdown
                  isReadOnly={!canEdit || !customCanEdit || amkaMapped}
                  name="guardianId"
                  label="Έμμεσα Ασφαλισμένο σε"
                  options={guardianOptions || []}
                  valueName="id"
                  labelName="fullName"
                  defaultFromProps={false}
                  changeFn={(e) => { }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  name="lastName"
                  label="Επώνυμο"
                  disabled={!canEdit || !customCanEdit}
                  defaultFromProps={false}
                />
              </Grid>
              {/* Row 2: firstName, gender */}
              <Grid item xs={12} md={6}>
                <TextInput
                  name="firstName"
                  label="Όνομα"
                  disabled={!canEdit || !customCanEdit}
                  defaultFromProps={false}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Dropdown
                  name="gender.id"
                  options={lookups?.gender}
                  label="Φύλο"
                  labelName="name"
                  valueName="id"
                  changeFn={(e) => {
                    findLookupObject(
                      lookups?.gender,
                      e.target.value,
                      "gender.name"
                    );
                  }}
                  isReadOnly={!canEdit || !customCanEdit}
                />
              </Grid>
              {/* Row 3: dob, insuredTo */}
              <Grid item xs={12} md={4}>
                <DateInput
                  name="dob.datePickerFormatted"
                  label="Ημ/νία Γέννησης"
                  disabled={!canEdit || !customCanEdit}
                  defaultFromProps={false}
                  blurFn={(e) => {
                    try {
                      childForm.setValue(
                        "dob.dateShortFormatted",
                        e.replace(/-/g, "")
                      );
                    } catch (e) {
                      console.log(e);
                    }
                  }}
                  defaultValue={
                    childForm.getValues("dob.datePickerFormatted") || ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextInput
                  name="age"
                  label="Ηλικία"
                  value={age ?? ""}
                  disabled
                  defaultFromProps={false}
                />
              </Grid>
              <Grid item xs={12}>
                <RadioInput
                  name="category.id"
                  label="Ιδιότητα Τέκνου"
                  disabled={!canEdit}
                  defaultFromProps={false}
                  valueName="id"
                  labelName="name"
                  options={computedCategoryOptions}
                  changeFn={(e) => {
                    findLookupObject(
                      lookups?.childCategories,
                      e.target.value,
                      "category.name",
                      [
                        { name: "category.fileDataKey", label: "fileDataKey" },
                        {
                          name: "category.fileDataValue",
                          label: "fileDataValue",
                        },
                      ]
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ width: "90%", mx: "auto" }}>
          <Button
            variant="contained"
            color="secondary"
            sx={{ color: "#fefefe", marginRight: "auto" }}
            autoFocus
            onClick={handleClose}
          >
            Πίσω
          </Button>
          <Button
            sx={{}}
            onClick={() =>
              childForm.handleSubmit((data) => {
                if (amkaOptions.includes(data?.amka)) {
                  setDialog({
                    title: "Σφάλμα",
                    message:
                      "Βρίσκεστε στό τμήμα προσθήκης τέκνων.\nΤο ΑΜΚΑ που εισάγατε έχει δηλωθεί στον δικαιούχο ή σε έτερο γονέα.\nΠαρακαλώ ελέγξτε και προσπαθήστε ξανά.",
                    onAccept: () => setDialogOpen(false),
                  });
                  setDialogOpen(true);
                  return;
                }

                if (data?.category?.id === 8) {
                  setWarningDialog({
                    open: true,
                    title: "Προειδοποίηση",
                    message:
                      "Το Τέκνο με ιδιότητα 'Κανένα από τα παραπάνω' ενδέχεται να μην είναι επιλέξιμο για αυτό το πρόγραμμα ΛΑΕ ΟΠΕΚΑ. Εάν είναι απαραίτητο για την αίτηση σας (Τρίτεκνης ή Πολύτεκνης) θα πρέπει να εμπίπτει σε κάποια άλλη από τις υπάρχουσες κατηγορίες.\n Θέλετε να συνεχίσετε;",
                    onAccept: () => {
                      if (!editId) {
                        childForm.setValue("id", uuidv4());
                        childForm.setValue("isNew", true);
                      }
                      editId ? handleEditRow(data) : handleAddRow(data);
                      setParentIsValid(false);
                      setAmkaMapped(false);
                      handleClose();
                      setWarningDialogOpen(false);
                      setWarningDialog({ onAccept: () => { } });
                    },
                  });
                  setWarningDialogOpen(true);

                  return;
                }

                // Κανονικό flow χωρίς dialog
                if (!editId) {
                  childForm.setValue("id", uuidv4());
                  childForm.setValue("isNew", true);
                }
                editId ? handleEditRow(data) : handleAddRow(data);
                setParentIsValid(false);
                setAmkaMapped(false);
                handleClose();
              })()
            }
            variant="contained"
            autoFocus
          >
            {editId ? "Ενημέρωση" : "Προσθήκη"}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
